import React, { useCallback, useState } from 'react';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { DotsIndicator, LoadingDots } from '@sg/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { Loyalty } from '@order/features/loyalty';
import { Bag } from '@order/features/ordering';
import { useLoyaltyContent, useLoyaltyPointsRewards } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useBagLoyalty, type useOrderState } from '../../hooks';

/**
 * The loyalty section on the bag.
 */
export const BagLoyalty = (props: BagLoyaltyProps) => {
  const { orderState } = props;

  // ─── Context ──────────────────────────────────────────────────────────────

  const {
    isFetchingRewards,
    hasFailedToLoadRewards,
    isApplyingPromoCode,
    isPerformingBagAction,
    rewardIdBeingUpdated,
    rewards,
    appliedRewardId,
    availablePoints,
    customer: { isNewCustomer, isOptedOutFromLoyalty },
    bagTracking: { trackLoyaltySignedOutPressed },
  } = orderState;

  // ─── Actions ──────────────────────────────────────────────────────────────

  const {
    showLoyaltyInfo,
    showLoyaltyTerms,
    applyBagReward,
    removeBagReward,
    refetchRewards,
    applyBagPromoCode,
  } = orderState;

  // ─── Auth State ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const navigation = useNavigation();

  const handleCreateAccount = useCallback(() => {
    trackLoyaltySignedOutPressed();
    navigation.navigate('Auth', {
      screen: 'Login',
      params: { redirect: 'bag' },
    });
  }, [navigation, trackLoyaltySignedOutPressed]);

  // ─── Promo Code New Customer Filtering ────────────────────────────────────

  const isPromoCodeNewCustomerFilteringEnabled = useFeatureFlag(
    'CELS-2917-reject-promo-code-previous-orders-enabled',
  );

  const shouldShowPromoCodeField =
    isLoggedIn && (!isPromoCodeNewCustomerFilteringEnabled || isNewCustomer);

  // ─── Contentful ───────────────────────────────────────────────────────────

  const {
    bagHeaderLoggedOut: loyaltyTitleLoggedOut,
    bagHeaderNewUser: loyaltyTitleNewUser,
    bagHeaderRecurringUser: loyaltyTitleRecurringUser,
    bagHeaderSubtitle: loyaltySubtitle,
    bagHeaderLoggedOutImageMessage: loyaltyImageText,
    bagHeaderLoggedOutImage: loyaltyImage,
  } = useLoyaltyContent({ availablePoints });

  const { title: loyaltyHeaderTitle } = useBagLoyalty({
    isLoggedIn,
    isNewCustomer: availablePoints === 0,
    loyaltyTitleLoggedOut,
    loyaltyTitleNewUser,
    loyaltyTitleRecurringUser,
    availablePoints,
  });

  // ─── Dynamic Subtitle ─────────────────────────────────────────────────────

  const { pointsRewards } = useLoyaltyPointsRewards();

  // ─── Flags ────────────────────────────────────────────────────────────────

  const hasLoyaltyV2Experience = !isOptedOutFromLoyalty;

  const hasRewards = rewards.length > 0;
  const shouldShowLoadingIndicator = !hasRewards && isFetchingRewards;
  const shouldShowEmptyState =
    isLoggedIn &&
    !hasRewards &&
    !isFetchingRewards &&
    !hasFailedToLoadRewards &&
    !isOptedOutFromLoyalty;
  const shouldShowInfoIcon = isLoggedIn;

  // ─── Scroll Indicator ─────────────────────────────────────────────────────

  const [currentRailWidth, setCurrentRailWidth] = useState(0);
  const [currentlyScrolledReward, setCurrentlyScrolledReward] = useState(0);
  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const scrollAmount = event.nativeEvent.contentOffset.x;
      const itemWidth = currentRailWidth / rewards.length;
      const halfWidth = itemWidth / 2;
      const scrolledReward = Math.floor((scrollAmount + halfWidth) / itemWidth);
      const isScrollingToLastReward = scrolledReward === rewards.length - 2;
      const shouldIndicateLastReward = currentRailWidth - scrollAmount < 500;
      const rewardToIndicate =
        isScrollingToLastReward && shouldIndicateLastReward
          ? rewards.length - 1
          : scrolledReward;

      setCurrentlyScrolledReward(rewardToIndicate);
    },
    [rewards.length, currentRailWidth],
  );

  if (isOptedOutFromLoyalty && !shouldShowPromoCodeField) return null;

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Bag.PromoRewardsContainer>
      {hasLoyaltyV2Experience ? (
        <Loyalty.Bag.LoyaltyHeader
          title={loyaltyHeaderTitle}
          onPressInfo={shouldShowInfoIcon ? showLoyaltyInfo : undefined}
        />
      ) : null}

      {hasLoyaltyV2Experience && !isLoggedIn ? (
        <Loyalty.Bag.LoyaltySignedOutCta
          message={loyaltyImageText}
          imageUrl={loyaltyImage}
          onPress={handleCreateAccount}
        />
      ) : null}

      {hasLoyaltyV2Experience && hasFailedToLoadRewards ? (
        <Loyalty.Bag.LoyaltyErrorState refetch={refetchRewards} />
      ) : null}

      {hasLoyaltyV2Experience && hasRewards ? (
        <Bag.RewardsRail
          heading={loyaltySubtitle}
          onScroll={handleOnScroll}
          onContentSizeChange={setCurrentRailWidth}
        >
          {rewards.map((reward) => {
            const isApplied = appliedRewardId === reward.id;
            const isLoading = rewardIdBeingUpdated === reward.id;
            const isDisabled = isPerformingBagAction && !isLoading;
            const onPress = isApplied ? removeBagReward : applyBagReward;

            const onShowTerms = reward.terms
              ? () => {
                  showLoyaltyTerms(reward.id);
                }
              : undefined;

            return (
              <Loyalty.Reward
                key={reward.id}
                id={reward.id}
                title={reward.name}
                points={reward.points}
                imageUrl={reward.assetUrl}
                expirationDate={reward.expirationDate}
                redeemableAt={reward.redeemableAt}
                isApplied={isApplied}
                isLoading={isLoading}
                isDisabled={isDisabled}
                onPress={onPress}
                onShowTerms={onShowTerms}
              />
            );
          })}
        </Bag.RewardsRail>
      ) : null}

      {hasLoyaltyV2Experience && rewards.length > 1 ? (
        <DotsIndicator
          length={rewards.length}
          active={currentlyScrolledReward}
        />
      ) : null}

      {shouldShowLoadingIndicator ? <LoadingDots /> : null}

      {shouldShowEmptyState ? (
        <Bag.RewardsEmptyState
          pointsRewards={pointsRewards}
          availablePoints={availablePoints}
        />
      ) : null}

      {shouldShowPromoCodeField ? (
        <Bag.PromoField
          rewards={rewards}
          isLoading={isApplyingPromoCode}
          onPress={applyBagPromoCode}
          isDisabled={Boolean(isPerformingBagAction && !isApplyingPromoCode)}
        />
      ) : null}
    </Bag.PromoRewardsContainer>
  );
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagLoyaltyProps = {
  orderState: ReturnType<typeof useOrderState>;
};
